import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  createPullRequest, decideItems, fetchNotes, fetchOpenThreads, fetchShippable,
  fetchSources, fetchSubmitted, threadKey,
  type PrResult, type SubmittedBatch,
} from '../lib/api';
import type { BatchItem, Note, OpenThread } from '../types';
import { LOCALE_META } from '../lib/locales';
import Avatar from '../components/Avatar';
import NoteThread from '../components/NoteThread';

const KIND_LABEL = { fix: 'Text change', ok: 'Confirmed', question: 'Question' } as const;
const KIND_TONE = { fix: 'p-acc', ok: 'p-ok', question: 'p-warn' } as const;

/** Owner only: read what reviewers sent, decide it, answer it, ship the fixes. */
export default function Submitted({ meId, onChanged }: { meId: string; onChanged?: () => void }) {
  const [batches, setBatches] = useState<SubmittedBatch[]>([]);
  const [sources, setSources] = useState<Map<string, string>>(new Map());
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pr, setPr] = useState<PrResult | null>(null);
  const [shippable, setShippable] = useState<BatchItem[]>([]);
  const [threads, setThreads] = useState<OpenThread[]>([]);
  const [notes, setNotes] = useState<Map<string, Note[]>>(new Map());

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [bs, ship, open] = await Promise.all([
        fetchSubmitted(), fetchShippable(), fetchOpenThreads(),
      ]);
      setBatches(bs);
      setShippable(ship);
      setThreads(open);

      const questionUnits = bs.flatMap(
        (b) => b.items.filter((i) => i.kind === 'question').map((i) => i.unit_id));
      const conversing = [...questionUnits, ...open.map((t) => t.unit_id)];

      const [srcs, thread] = await Promise.all([
        fetchSources([...new Set([
          ...bs.flatMap((b) => b.items.map((i) => i.unit_id)),
          ...ship.map((i) => i.unit_id),
          ...open.map((t) => t.unit_id),
        ])]),
        fetchNotes(conversing),
      ]);
      setSources(srcs);

      const grouped = new Map<string, Note[]>();
      for (const n of thread) {
        const k = threadKey(n.unit_id, n.locale);
        (grouped.get(k) ?? grouped.set(k, []).get(k)!).push(n);
      }
      setNotes(grouped);
      setChosen(new Set());
      onChanged?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally { setLoading(false); }
  }, [onChanged]);

  useEffect(() => { void load(); }, [load]);

  const pending = useMemo(
    () => batches.flatMap((b) => b.items.filter((i) => i.decision === 'pending')),
    [batches],
  );

  // A question still sitting in a batch is answered down in that batch, where
  // its approve and reject buttons are. This section is for the rest: replies
  // to an answer, and notes left on prompts nobody has queued anything for.
  const loose = useMemo(() => {
    const queued = new Set(batches.flatMap((b) => b.items
      .filter((i) => i.kind === 'question' && i.decision === 'pending')
      .map((i) => threadKey(i.unit_id, i.locale))));
    return threads.filter((t) => !queued.has(threadKey(t.unit_id, t.locale)));
  }, [batches, threads]);

  const toggle = (id: string) =>
    setChosen((c) => {
      const n = new Set(c);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  async function decide(decision: 'approved' | 'rejected') {
    if (!chosen.size) return;
    setBusy(true); setError(null);
    try { await decideItems([...chosen], decision); await load(); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }

  async function ship() {
    setBusy(true); setError(null);
    try { setPr(await createPullRequest()); await load(); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }

  return (
    <>
      <div className="phead">
        <div>
          <h1>From reviewers</h1>
          <p>
            What your reviewers have sent. Nothing here has touched the repo — approving a text
            change queues it for a pull request, and approving a “looks good” records that someone
            fluent read it and it was fine.
          </p>
        </div>
        <div className="acts">
          <button className="btn" onClick={() => void load()} disabled={busy}>Refresh</button>
          <button
            className="btn pri"
            onClick={() => void ship()}
            disabled={busy || !shippable.length}
            title={shippable.length
              ? `${shippable.length} approved change${shippable.length === 1 ? '' : 's'} not yet in a pull request`
              : 'Approve a text change first — there is nothing to ship'}
          >
            {busy ? 'Working…'
              : shippable.length
                ? `Open pull request (${shippable.length} staged)`
                : 'Open pull request'}
          </button>
        </div>
      </div>

      {pr && (
        <div className="sent" style={{ marginBottom: 16 }}>
          <b>Pull request #{pr.number} is open</b>
          <p>
            {pr.items} fix{pr.items === 1 ? '' : 'es'} across {pr.files.length}{' '}
            file{pr.files.length === 1 ? '' : 's'}. The checks run now; merge it when they pass.
          </p>
          <div style={{ marginTop: 12 }}>
            <a className="btn pri" href={pr.url} target="_blank" rel="noreferrer">View on GitHub</a>
          </div>
        </div>
      )}

      {error && (
        <div className="banner" style={{ background: 'var(--crit-soft)', borderColor: 'var(--crit-line)' }}>
          <span style={{ color: 'var(--crit)', fontWeight: 800 }}>!</span>
          <span>
            <span className="t" style={{ color: 'var(--crit)' }}>That did not work</span>
            <span className="d" style={{ whiteSpace: 'pre-wrap' }}>{error}</span>
          </span>
        </div>
      )}

      {!loading && loose.length > 0 && (
        <div className="bsec">
          <div className="bsec-h">
            <span className="pill p-warn">{loose.length}</span>
            <span>Waiting on you</span>
            <span className="d">Threads whose last word was somebody else's</span>
          </div>
          {loose.map((t) => (
            <div className="sug" key={threadKey(t.unit_id, t.locale)}>
              <span className="body">
                <span className="rmeta">
                  <span className="rid">{t.prompt_id ?? t.unit_id.split('/').slice(1).join('/')}</span>
                  <span className="right">
                    <span className="pill p-acc">{LOCALE_META[t.locale]?.native ?? t.locale}</span>
                    {t.unanswered && <span className="pill p-warn">Unanswered</span>}
                  </span>
                </span>
                <span className="sins">{t.source_text}</span>
                <NoteThread
                  notes={notes.get(threadKey(t.unit_id, t.locale)) ?? []}
                  unitId={t.unit_id}
                  locale={t.locale}
                  meId={meId}
                  onPosted={() => void load()}
                />
              </span>
            </div>
          ))}
        </div>
      )}

      {loading && <div className="panel"><div className="empty">Loading…</div></div>}

      {!loading && !batches.length && !loose.length && (
        <div className="panel"><div className="empty">
          <b>Nothing waiting to be decided</b>
          {shippable.length
            ? `${shippable.length} approved change${shippable.length === 1 ? '' : 's'} ${
                shippable.length === 1 ? 'is' : 'are'} staged and not yet in a pull request — use the button above.`
            : 'When a reviewer sends a batch it appears here.'}
        </div></div>
      )}

      {batches.map((b) => (
        <div className="bsec" key={b.id}>
          <div className="bsec-h">
            <Avatar name={b.member?.display_name ?? 'Someone'} url={b.member?.avatar_url} size={26} />
            <span>{b.member?.display_name ?? 'Someone'}</span>
            <span className="d">
              {b.items.length} item{b.items.length === 1 ? '' : 's'} ·{' '}
              {new Date(b.submitted_at).toLocaleDateString()}
            </span>
            <span className="spacer" style={{ flex: 1 }} />
            <button
              className="btn sm"
              onClick={() => setChosen((c) => {
                const n = new Set(c);
                b.items.filter((i) => i.decision === 'pending').forEach((i) => n.add(i.id!));
                return n;
              })}
            >
              Select all
            </button>
          </div>

          {b.note && <p className="why" style={{ margin: '0 2px 8px' }}>“{b.note}”</p>}

          {b.items.map((i) => {
            const decided = i.decision !== 'pending';
            return (
              <label className="sug" key={i.id} style={decided ? { opacity: 0.55 } : undefined}>
                <input
                  type="checkbox" disabled={decided || busy}
                  checked={chosen.has(i.id!)} onChange={() => toggle(i.id!)}
                />
                <span className="body">
                  <span className="rmeta">
                    <span className="rid">{i.unit_id.split('/')[1] ?? i.unit_id}</span>
                    <span className="right">
                      <span className="pill p-acc">{LOCALE_META[i.locale]?.native ?? i.locale}</span>
                      <span className={`pill ${KIND_TONE[i.kind]}`}>{KIND_LABEL[i.kind]}</span>
                      {decided && (
                        <span className={`pill ${i.decision === 'approved' ? 'p-ok' : 'p-mute'}`}>
                          {i.decision}
                        </span>
                      )}
                    </span>
                  </span>

                  {/* The English is the only thing that makes a fix judgeable. */}
                  <span className="sins">{sources.get(i.unit_id) ?? '—'}</span>

                  {i.kind === 'fix' && (
                    <span className="diff">
                      <span className="o">{i.from_text}</span>
                      <span className="n">{i.to_text}</span>
                    </span>
                  )}
                  {i.kind === 'question' && (
                    <NoteThread
                      notes={notes.get(threadKey(i.unit_id, i.locale)) ?? []}
                      unitId={i.unit_id}
                      locale={i.locale}
                      meId={meId}
                      onPosted={() => void load()}
                      placeholder="Answer this — it lands on the prompt, so the next reviewer sees it too."
                    />
                  )}
                  {i.kind === 'ok' && (
                    <span className="con" style={{ fontSize: 12, color: 'var(--muted)' }}>
                      Confirmed correct — approving records the sign-off, no text changes.
                    </span>
                  )}
                </span>
              </label>
            );
          })}
        </div>
      ))}

      {pending.length > 0 && (
        <div className="cartbar">
          <span className="tally">
            <span>{chosen.size} <i>of {pending.length} selected</i></span>
          </span>
          <span className="spacer" style={{ flex: 1 }} />
          <button className="btn" disabled={!chosen.size || busy} onClick={() => void decide('rejected')}>
            Reject
          </button>
          <button className="btn pri" disabled={!chosen.size || busy} onClick={() => void decide('approved')}>
            Approve {chosen.size || ''}
          </button>
        </div>
      )}
    </>
  );
}
